import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

export function isNotLoggedIn(
  {
    impersonatorEmail,
    impersonatorUserToken,
    loggedIn,
    hasStrongAuthentication,
    isTwoFactorAuthenticated,
    isTwoFactorAuthEnabled,
    userToken,
    email,
  }: RentalManagerAuthCheckUserInfoWebApiDto,
  requireStrongAuthentication = false,
) {
  const isImpersonating = impersonatorEmail && impersonatorUserToken;

  if (!loggedIn || (!isImpersonating && loggedIn && !email && !userToken)) {
    return true;
  } else if (isImpersonating) {
    return false;
  } else if (requireStrongAuthentication && !hasStrongAuthentication) {
    return true;
  } else if (requireStrongAuthentication && isTwoFactorAuthEnabled && !isTwoFactorAuthenticated) {
    return true;
  }
}

export function hasRequiredRoles({
  allRequiredRoles = [],
  oneOfRequiredRoles = [],
  userRoles = [],
}: {
  allRequiredRoles?: Array<string>;
  oneOfRequiredRoles?: Array<string>;
  userRoles: Array<string>;
}) {
  let hasAccess = true;

  if (allRequiredRoles.length && !allRequiredRoles.every((r) => userRoles.includes(r))) {
    hasAccess = false;
  }

  if (oneOfRequiredRoles.length && !oneOfRequiredRoles.some((r) => userRoles.includes(r))) {
    hasAccess = false;
  }

  return hasAccess;
}

/**
 * Resolves a relative auth base URL (e.g. `/` or `/path`) to a full origin URL in the browser.
 * Returns the raw string unchanged when not in browser or when already absolute.
 */
export function resolveAuthBaseUrl(raw: string): string {
  if (!raw.startsWith('/') || typeof window === 'undefined') return raw;
  return raw === '/' ? window.location.origin : `${window.location.origin}${raw}`;
}

// not as heavy as full url encoding
// doesn't encode '/'
// encode '%', '&', '?' and '#' in proper order
export function partialUrlEncode(str: string) {
  return str.replaceAll('%', '%25').replaceAll('&', '%26').replaceAll('?', '%3F').replaceAll('#', '%23');
}

export function loginUrl({
  crossEnv,
  host = 'https://www.zillow.com',
  port,
  protocol = 'http',
  redirectUrl,
}: {
  crossEnv: boolean;
  host?: string;
  port?: number;
  protocol?: string;
  redirectUrl: string;
}) {
  let path = partialUrlEncode(redirectUrl);

  if (crossEnv) {
    path = `/rental-manager-api/forward?protocol=${protocol}&port=${port ?? ''}&path=${path}`;
    path = partialUrlEncode(path);
  }

  if (host.endsWith('/')) {
    host = host.slice(0, -1);
  }

  return `${host}/user/acct/login/?url=${path}`;
}
